// Formulas from the design doc ("Borba i formula snage"). Everything that turns
// a survivor into numbers lives here, so server, client and simulations agree.

import { GAME, STACK_CAPS, ENERGY_RULES } from './constants';
import { BACKGROUNDS_DEF } from './content/backgrounds';
import { TIER_LEVELS, isSlotLocked, itemTemplate } from './content/items';
import { STANCES_DEF } from './content/stances';
import { learnedNodes, treeBonusesOf, treePowerFactor, type SkillStat } from './skills/tree';
import { itemStats } from './itemgen';
import { getActiveSetBonuses } from './items/sets';
import { loadEffects, loadInfo } from './items/load';
import { SET_HOOK_TUNING, militaryZone } from './items/setHooks';
import { PERK_POWER } from './items/perkPower';
import { wornCounts } from './items/durability';
import { wornEquipment } from './items/tierWear';
import { weaponFeed } from './items/feed';
import { mealBonus, mealBoostWithTier } from './meals';

/** 7. 10. 2026: Hollow-point Rounds, +10% (supplies.ts `HOLLOW_POINT`; a test keeps the two the same). */
export const HOLLOW_POINT_DAMAGE = 0.1;
import { SIGNATURE_PASSIVE_ID, itemPerkEffects, perkFlag, perkSum, type ActivePerkEffect, type PerkConditions, type PerkEffect, type PerkEffectType } from './items/perks';
import { rankLoot } from './rank';
import { clamp } from './rng';
import type { Attributes, BackgroundId, CombatStats, EnemyType, Equipment, GearSlot, ItemStats, SkillRanks, Stance, WeaponClass, ZoneId } from './types';
import { ATTRS } from './types';
import { TRUCK_RULES } from './fuelUses';

export interface SurvivorBuild {
  level: number;
  attrs: Attributes;
  /** Learned skill tree nodes, `{ nodeId: order }` (krug 5, K10). */
  skills: SkillRanks;
  background: BackgroundId;
  equipment: Equipment;
  /** Survivor Rank after level 20 (krug 5, F12): only +1% loot every 10 ranks. */
  rank?: number;
}

export interface BuildContext {
  stance?: Stance;
  /** Runs sent before K12 (hunger is gone for new ones). */
  hungry?: boolean;
  /** Krug 5 (K12.3): the meal eaten before the run (max HP, escape, XP here; the rest where it acts). Not in Power. */
  meal?: import('./meals').MealId | null;
  /** Painkillers: +20% defense for the expedition. */
  painkillers?: boolean;
  /** Stim: +3 AGI for the expedition. */
  stim?: boolean;
  /** 7. 10. 2026: Hollow-point Rounds: +10% attack with a firearm for the expedition (`HOLLOW_POINT`). */
  hollowPoint?: boolean;
  /** 7. 10. 2026 (round 2): the truck took the survivor: more pack room for the expedition (`TRUCK_RULES.capacity`, +20% since 9. 10.). */
  truck?: boolean;
  /** Clan research and other flat bonuses. */
  extra?: Partial<Pick<CombatStats, 'escape' | 'loot' | 'xp'>>;
  /** Zone of the expedition (Military 5 works only in some zones). */
  zone?: ZoneId;
  /** A clan raid run (Valor: +% to all stats, krug 5 F10). */
  raid?: boolean;
  /** Package 4, part C: injuries and fever on now (after Power, like a meal). */
  conditions?: import('./conditions').ConditionMods;
}

// ---- The raw formulas ------------------------------------------------------

export const hpFormula = (vit: number, level: number, hpGear: number) => 100 + 20 * vit + 10 * level + hpGear;
/** `stat` is STR for melee and PER for ranged weapons (krug 5, F8.1). */
export const attackFormula = (weaponDmg: number, stat: number, level: number) => weaponDmg * (1 + stat / 100) + 2 * level;

/**
 * Diminishing returns (krug 5, F8.2): every attribute point above 40 counts half, for all
 * derived stats and event checks. 52 counts as 46.
 */
export function effectiveAttr(x: number): number {
  return Math.min(x, GAME.ATTR_SOFT_CAP) + Math.max(0, x - GAME.ATTR_SOFT_CAP) / 2;
}
export const defenseFormula = (armorGear: number, vit: number) => armorGear + 0.5 * vit;
/**
 * Power (krug 5, F4): one number for how strong a survivor is in a fight, fitted on simulated
 * duels (pnpm sim:power). A fight is won by dealing a bigger share of the other's HP than one
 * takes (at levels 5 and 10 every duel goes the full 10 rounds), so strength is
 *   √(offense × durability) × weapon class × (1 + 0.02 × initiative) × the gear's perk factors
 *   offense    = attack × (1 + crit × (crit multiplier − 1)) × hit chance against a reference AGI
 *   durability = HP × (1 + defense / 100) ÷ the hit chance a reference PER has against you
 * The reference AGI and PER grow with the level (15 + 1.2 per level). Perk factors come from
 * pnpm sim:perks (items/perk_power.json): what a perk wins in a fight beyond the stats it
 * already raises. Power = scale × strength^exponent: fights are close to deterministic (a 25%
 * stronger survivor wins ~93%), and with the exponent 2.7 a survivor with 25% more Power wins
 * about 72% of duels, equal Power (±5%) about 45–55% (F4 targets 65–80% and 40–60%).
 */
export const POWER_MODEL = {
  refBase: 23.2,
  refPerLevel: 0.158,
  /** Share of offense in strength (0.5 = the plain square root). */
  offense: 0.526,
  /** Weight of the hit chances in offense and durability (1 = as they are). */
  hitWeight: 0.921,
  /** Krug 5 (K13): fitted again with the new combat rules (before: 1.046, 1.013, 1.07). */
  cls: { melee_1h: 1, melee_2h: 1.046, ranged_1h: 1.013, ranged_2h: 1.07 } as Record<WeaponClass, number>,
  initiative: 0.208,
  /** K13: 3.0 (2.7 before), so +25% Power wins about 68% with the new combat rules. */
  exponent: 3,
  /** A level 1 survivor in starting gear (strength about 97) has about 100 Power. */
  scale: 100 / Math.pow(97, 3),
};

export interface StrengthInput {
  level: number;
  /** Attack before stance and hunger. */
  attack: number;
  crit: number;
  critMult: number;
  accuracy: number;
  dodge: number;
  per: number;
  agi: number;
  hpMax: number;
  defense: number;
  weaponClass: WeaponClass | null;
  initiative: number;
}

/** Fighting strength without perk factors (the part of Power the stats explain). */
export function combatStrength(i: StrengthInput): number {
  const ref = POWER_MODEL.refBase + POWER_MODEL.refPerLevel * (i.level - 1);
  const hit = clamp(GAME.HIT_BASE + COMBAT_TUNING.hitPerPoint * (i.per - ref) + i.accuracy, GAME.HIT_MIN, GAME.HIT_MAX);
  const taken = clamp(GAME.HIT_BASE + COMBAT_TUNING.hitPerPoint * (ref - i.agi) - i.dodge, GAME.HIT_MIN, GAME.HIT_MAX);
  const offense = Math.max(1, i.attack) * (1 + i.crit * (i.critMult - 1)) * Math.pow(hit, POWER_MODEL.hitWeight);
  const durability = (Math.max(1, i.hpMax) * (1 + Math.max(0, i.defense) / 100)) / Math.pow(taken, POWER_MODEL.hitWeight);
  const a = POWER_MODEL.offense;
  return Math.pow(offense, a) * Math.pow(durability, 1 - a) * (i.weaponClass ? POWER_MODEL.cls[i.weaponClass] : 1) * (1 + POWER_MODEL.initiative * i.initiative);
}

/** The strength of a finished stat block (no stance or hunger in it), without perk factors. */
export function strengthOfStats(s: CombatStats): number {
  return combatStrength({ level: s.level, attack: s.attack, crit: s.crit, critMult: s.critMult, accuracy: s.accuracy, dodge: s.dodge, per: s.per, agi: s.agi, hpMax: s.hpMax, defense: s.defense, weaponClass: s.weaponClass, initiative: s.initiative ?? 0 });
}

export function powerFromStrength(strength: number): number {
  return Math.round(POWER_MODEL.scale * Math.pow(Math.max(0, strength), POWER_MODEL.exponent));
}

/** Product of the perk factors of worn items (items/perk_power.json); a broken item's perk works (7. 10. 2026). */
export function perkPowerFactor(equipment: Equipment): number {
  let f = 1;
  for (const [slot, it] of Object.entries(equipment) as [GearSlot, Equipment[GearSlot]][]) {
    if (!it || isSlotLocked(slot) || !wornCounts(it) || it.passive?.id !== SIGNATURE_PASSIVE_ID) continue;
    f *= PERK_POWER[`${it.templateId}:${it.rarity}`] ?? 1;
  }
  return f;
}

/**
 * Hit chance per point of PER over the target's AGI (design doc start value: 0.01), and the
 * attribute ranged damage grows with (krug 5, F8.1: PER; the balance simulation can compare STR).
 */
export const COMBAT_TUNING: { hitPerPoint: number; rangedAttackStat: 'per' | 'str' } = { hitPerPoint: 0.006, rangedAttackStat: 'per' };

export function hitChance(perA: number, agiB: number, accuracyA = 0, dodgeB = 0): number {
  return clamp(GAME.HIT_BASE + COMBAT_TUNING.hitPerPoint * (perA - agiB) + accuracyA - dodgeB, GAME.HIT_MIN, GAME.HIT_MAX);
}

export function escapeChance(agiRun: number, agiChase: number, carried: number, capacity: number, bonus = 0, packPenaltyMult = 1): number {
  const load = capacity > 0 ? clamp(carried / capacity, 0, 1) : 0;
  return clamp(
    GAME.ESCAPE_BASE + 0.02 * (agiRun - agiChase) - GAME.ESCAPE_FULL_PACK_PENALTY * load * packPenaltyMult + bonus,
    GAME.ESCAPE_MIN,
    GAME.ESCAPE_MAX,
  );
}

export function detectChance(perTarget: number, agiShadow: number, stealthShadow: number, detectBonus = 0): number {
  return clamp(GAME.DETECT_BASE + 0.02 * (perTarget - agiShadow) - stealthShadow + detectBonus, GAME.DETECT_MIN, GAME.DETECT_MAX);
}

// ---- Survivor -> numbers -----------------------------------------------------

export function baseAttributes(background: BackgroundId): Attributes {
  const bg = BACKGROUNDS_DEF[background];
  const a = {} as Attributes;
  for (const k of ATTRS) a[k] = GAME.BASE_ATTR + (bg.bonus[k] ?? 0);
  return a;
}

export interface GearTotals {
  stats: ItemStats;
  passives: string[];
  weaponDamage: number;
  weaponClass: CombatStats['weaponClass'];
  loud: boolean;
  /** 7. 10. 2026: the worn weapon fires Ammo. */
  firearm: boolean;
  /** Perk effects of the worn gear (krug 4; a broken item's too since 7. 10. 2026). */
  perkFx: ActivePerkEffect[];
}

/** Weapon class rules (plan v0.6 E3, krug 5 F1). */
export const WEAPON_CLASS_RULES = {
  oneHanded: { initiative: 0.1, escape: 0.1 },
  twoHanded: { escape: -0.05 },
  /** Only melee two-handers ignore part of the target's defense. */
  meleeTwoHanded: { ignoreDefense: 0.15 },
  /** A heavy gun is slower to bring up. */
  rangedTwoHanded: { initiative: -0.1 },
  /** The opening shot of round 0 (not an ambush): 60% damage and no critical hit. */
  openingShot: { damage: 0.6 },
} as const;

export function isOneHanded(c: CombatStats['weaponClass']): boolean {
  return c === 'melee_1h' || c === 'ranged_1h';
}

export function isTwoHanded(c: CombatStats['weaponClass']): boolean {
  return c === 'melee_2h' || c === 'ranged_2h';
}

/**
 * Perk effects whose conditions are known when the stats are made (zone and stance of the
 * expedition, the survivor's own weapon type). Those fold into the numbers; effects that
 * depend on the moment (HP, target, run length, who strikes first) are read later.
 */
function staticSum(fx: readonly PerkEffect[], type: PerkEffectType, c: PerkConditions, pred: (e: PerkEffect) => boolean = () => true): number {
  const live = fx.filter((e) => e.belowHpPct == null && e.minDurationMin == null && !e.targets && !e.whenStrikingFirst && pred(e));
  return perkSum(live, type, c);
}

export function gearTotals(equipment: Equipment): GearTotals {
  const stats: ItemStats = {};
  const passives: string[] = [];
  let weaponDamage: number = GAME.UNARMED_DAMAGE;
  let weaponClass: CombatStats['weaponClass'] = null;
  let loud = false;
  let firearm = false;
  const perkFx: ActivePerkEffect[] = [];
  for (const [slot, item] of Object.entries(equipment) as [GearSlot, Equipment[GearSlot]][]) {
    if (!item || isSlotLocked(slot)) continue;
    // 7. 10. 2026 (owner): a broken item stays in the fight: its stats at 75% (itemStats), its perk, its weapon class.
    const s = itemStats(item);
    if (!wornCounts(item)) continue;
    const t = itemTemplate(item.templateId);
    if (slot === 'weapon') {
      // Flat damage affixes (old items) add to the weapon's damage.
      weaponDamage = s.damage ?? GAME.UNARMED_DAMAGE;
      weaponClass = t.weaponClass ?? null;
      loud = !!t.loud;
      firearm = weaponFeed(item.templateId) === 'ammo';
    }
    for (const [k, v] of Object.entries(s) as [keyof ItemStats, number][]) {
      if (k === 'damage') continue;
      stats[k] = (stats[k] ?? 0) + v;
    }
    if (item.passive) passives.push(item.passive.id);
    perkFx.push(...itemPerkEffects(item));
  }
  return { stats, passives, weaponDamage, weaponClass, loud, firearm, perkFx };
}

/** Full stat block. Without a context it is the "profile" view (no stance effects). */
export function deriveStats(b0: SurvivorBuild, ctx: BuildContext = {}): CombatStats {
  // 23. 10. 2026 (owner, BACKLOG #55): gear above the survivor's tier counts as the top of the survivor's tier until its
  // level (`wornEquipment`, items/tierWear.ts). Here, so every fight and every Power sees the same numbers.
  const worn = wornEquipment(b0.equipment, b0.level);
  const b = worn === b0.equipment ? b0 : { ...b0, equipment: worn };
  const g = gearTotals(b.equipment);
  const sets = getActiveSetBonuses(b.equipment);
  const set = sets.stats;
  const bg = BACKGROUNDS_DEF[b.background];
  const stance = ctx.stance ? STANCES_DEF[ctx.stance].mods : {};
  const has = (p: string) => g.passives.includes(p);
  // Item perks (krug 4): plain effects and those whose zone / stance / weapon type is known now.
  const fx = g.perkFx;
  const ownAttack = g.weaponClass === 'ranged_1h' || g.weaponClass === 'ranged_2h' ? 'ranged' : 'melee';
  const pc: PerkConditions = { zone: ctx.zone, stance: ctx.stance, attack: ownAttack };
  const pk = (type: PerkEffectType, pred?: (e: PerkEffect) => boolean) => staticSum(fx, type, pc, pred);
  // Military 5: +10% to every stat in the Military Base and open-PvP zones. Valor (F10): +% to
  // every stat on a clan raid run, the same way.
  const zb =
    (sets.hooks.includes('military_zone') && militaryZone(ctx.zone) ? 1 + SET_HOOK_TUNING.militaryZoneBoost : 1) * (ctx.raid ? 1 + pk('allStatsPctDuringRaid') / 100 : 1);
  const flat = (stat: string) => pk('statFlat', (e) => e.stat === stat);
  const oneH = isOneHanded(g.weaponClass);
  const twoH = isTwoHanded(g.weaponClass);
  // The skill tree (krug 5, K10): its sums by stat (skills/tree.ts, one function for server, sims and UI).
  const tree = treeBonusesOf(b.skills);
  const tb = (k: SkillStat) => tree[k] ?? 0;
  const allAttr = tb('allAttributes');

  // Points, gear and perks; above 40 a point counts half (krug 5, F8.2). Everything below
  // uses the effective values. "+% STAT" perks (Owl Eye, F10) scale the sum.
  const pctOf = (stat: string) => 1 + pk('statPct', (e) => e.stat === stat) / 100;
  const total: Attributes = {
    str: Math.round((b.attrs.str + (g.stats.str ?? 0) + flat('STR') + allAttr) * pctOf('STR')),
    agi: Math.round((b.attrs.agi + (g.stats.agi ?? 0) + set.agi + flat('AGI') + allAttr) * pctOf('AGI')),
    int: Math.round((b.attrs.int + (g.stats.int ?? 0) + flat('INT') + allAttr) * pctOf('INT')),
    vit: Math.round((b.attrs.vit + (g.stats.vit ?? 0) + flat('VIT') + allAttr) * pctOf('VIT')),
    per: Math.round((b.attrs.per + (g.stats.per ?? 0) + set.per + flat('PER') + allAttr) * pctOf('PER')),
  };
  const strRaw = effectiveAttr(total.str);
  const agiRaw = effectiveAttr(total.agi);
  const intRaw = effectiveAttr(total.int);
  const vitRaw = effectiveAttr(total.vit);
  const perEff = effectiveAttr(total.per);
  // Equipment load: what is worn against what STR can carry (section 7).
  const load = loadInfo(b.equipment, strRaw);
  const lf = loadEffects(load.state);
  const str = Math.round(strRaw * zb);
  const agiNoStim = Math.round(agiRaw * zb);
  const agi = agiNoStim + (ctx.stim ? 3 : 0);
  const int = Math.round(intRaw * zb);
  const vit = Math.round(vitRaw * zb);
  const perRaw = Math.round(perEff * zb);
  const per = Math.round(perRaw * (1 + (stance.per ?? 0)));

  // Pants count toward Defense like every other armor piece (armor is summed over all slots).
  // The tree's flat HP adds to the sum, its % multiplies it (K10.3).
  const hpMax = Math.round((hpFormula(vitRaw, b.level, g.stats.hp ?? 0) + tb('maxHp')) * (1 + tb('maxHpPct') / 100) * zb);
  // Perks: +% damage for the survivor's own attack type and zone, +% defense.
  const perkDamage = (pk('damagePct') + pk('zoneDamagePct')) / 100;
  // "+% damage" affixes (E5) add to the set percentage. Melee damage grows with STR, ranged with PER (F8.1).
  const ranged = g.weaponClass === 'ranged_1h' || g.weaponClass === 'ranged_2h';
  const damageStat = ranged && COMBAT_TUNING.rangedAttackStat === 'per' ? perEff : strRaw;
  // Tree damage (K10.3): all damage, plus melee or ranged by the weapon (unarmed counts as melee); it adds to the perks.
  const treeDamage = (tb('damagePct') + (ranged ? tb('rangedDamagePct') : tb('meleeDamagePct'))) / 100;
  const attackBase = attackFormula(g.weaponDamage, damageStat, b.level) * (1 + set.attackPct + (g.stats.damagePct ?? 0)) * zb * (1 + perkDamage + treeDamage);
  // Phantom (K10.4): the armor of the worn gear gives only part of its Defense (VIT, sets and the tree stay).
  const armorMult = tree.armorDefenseMult ?? 1;
  const defenseBase = (defenseFormula((g.stats.armor ?? 0) * armorMult, vitRaw) + set.defense + tb('defense')) * zb * (1 + pk('defensePct') / 100 + tb('defensePct') / 100);
  const treeCrit = ranged ? tb('rangedCritPct') / 100 : 0;
  const critChance = Math.min(STACK_CAPS.crit, GAME.BASE_CRIT + (g.stats.crit ?? 0) + treeCrit + (has('steady_hands') ? 0.05 : 0) + pk('critPct') / 100);
  const critMult = has('ember') ? 2 : GAME.CRIT_MULT;
  const accuracy = Math.min(STACK_CAPS.accuracy, (g.stats.accuracy ?? 0) + pk('accuracyPct') / 100 + tb('accuracy') / 100);
  // Dodge stays between −10% and its cap: 30%, with Phantom 45% (K10.4).
  const dodgeCap = tree.dodgeCap ? tree.dodgeCap / 100 : DODGE_CAP;
  const dodge = clamp((g.stats.dodge ?? 0) + set.dodge + lf.dodge + pk('dodgePct') / 100 + tb('dodgePct') / 100, -0.1, dodgeCap);
  const initiative =
    lf.initiative + pk('initiativePct') / 100 + (oneH ? WEAPON_CLASS_RULES.oneHanded.initiative : g.weaponClass === 'ranged_2h' ? WEAPON_CLASS_RULES.rangedTwoHanded.initiative : 0);
  // The tree's initiative multiplies (K10.3): AGI × (1 + load and the rest) × (1 + initiativePct).
  const initiativeMult = 1 + tb('initiativePct') / 100;
  const openingShotDamage = tb('openingShotDamagePct') / 100;
  const openingShotCrit = tb('openingShotAlwaysCrit') > 0;
  // Nodes whose effect is not in the numbers (Adrenaline, Executioner, the opening shot) have a Power factor (K10.3).
  const treePower = treePowerFactor(learnedNodes(b.skills), ranged);
  // Power (F4): stance, hunger and the stim stay out of it.
  const strength =
    combatStrength({ level: b.level, attack: attackBase, crit: critChance, critMult, accuracy, dodge, per: perRaw, agi: agiNoStim, hpMax, defense: defenseBase, weaponClass: g.weaponClass, initiative: (1 + initiative) * initiativeMult - 1 }) *
    perkPowerFactor(b.equipment) *
    treePower;
  const power = powerFromStrength(strength);
  // Krug 5 (K12): the meal comes after Power (like the stance and the Stim), boosted by Iron Stomach and Camper.
  // Package 4, part O: and by the survivor's tier (T4 +30%); Station 9 Chili, Black Coffee and the Protein Bar.
  const tierNow = ([4, 3, 2, 1] as const).find((t) => b.level >= TIER_LEVELS[t][0]) ?? 1;
  const mealBoostNow = mealBoostWithTier(tb('mealEffectPct') + pk('mealEffectPct'), tierNow);
  const meal = mealBonus(ctx.meal, mealBoostNow);

  let attack = attackBase * (1 + (stance.attack ?? 0)) * (1 + meal.damagePct);
  if (ctx.hungry) attack *= 1 - GAME.HUNGRY_ATTACK_PENALTY;
  if (ctx.hollowPoint && g.firearm) attack *= 1 + HOLLOW_POINT_DAMAGE;
  let defense = defenseBase * (1 + (stance.defense ?? 0)) * (1 + meal.defensePct);
  if (ctx.painkillers) defense *= 1.2;

  let capacity = GAME.CARRY_BASE + strRaw + (g.stats.capacity ?? 0) + set.capacity + tb('capacity') + pk('capacityFlat') + pk('capacityNoPenaltyFlat');
  if (has('deep_pockets')) capacity *= 1.1;
  // 7. 10. 2026 (round 2): the truck's room for the haul.
  if (ctx.truck) capacity *= 1 + TRUCK_RULES.capacity;

  const intOver = Math.max(0, int - GAME.BASE_ATTR);
  // A silenced weapon (perk) is no longer loud: no extra fights, no stealth penalty.
  const loud = g.loud && !has('quiet_one') && !fx.some((e) => e.type === 'silent' && e.value === true);

  const vs: CombatStats['vs'] = {
    infected: (g.stats.vsInfected ?? 0) + (has('bane_infected') ? 0.1 : 0) + (has('ember') ? 0.25 : 0),
    raider: (g.stats.vsRaider ?? 0) + (has('bane_raiders') ? 0.1 : 0),
    mutant: (g.stats.vsMutant ?? 0) + set.vsMutant + (has('bane_mutants') ? 0.12 : 0) + (has('ember') ? 0.25 : 0),
    drone: g.stats.vsDrone ?? 0,
    survivor: (g.stats.vsSurvivor ?? 0) + (has('bane_survivors') ? 0.08 : 0),
  };

  // Package 4, part C: an injury or a fever (after Power).
  const cond = ctx.conditions;
  const hpCond = 1 + (cond?.hpMaxPct ?? 0);
  return {
    level: b.level,
    hpMax: meal.hpMaxPct || hpCond !== 1 ? Math.round(hpMax * (1 + meal.hpMaxPct) * hpCond) : hpMax,
    attack: Math.round(attack * 10) / 10,
    defense: Math.round(defense * 10) / 10,
    str,
    agi,
    int,
    vit,
    per,
    attrsTotal: total,
    power,
    capacity: Math.round(capacity),
    // F2: the accuracy bonus from all sources stops at +20 points. Package 4, part C: a Concussion takes from it.
    accuracy: cond?.accuracy ? accuracy + cond.accuracy : accuracy,
    crit: critChance,
    critMult,
    escape:
      (g.stats.escape ?? 0) + set.escape + lf.escape + tb('escapePct') / 100 + (has('fleet') ? 0.08 : 0) + (stance.escape ?? 0) + (ctx.extra?.escape ?? 0) + meal.escape +
      pk('escapePct') / 100 + (oneH ? WEAPON_CLASS_RULES.oneHanded.escape : twoH ? WEAPON_CLASS_RULES.twoHanded.escape : 0) + (cond?.escape ?? 0),
    loot: (g.stats.loot ?? 0) + (bg.perks.loot ?? 0) + (stance.loot ?? 0) + (has('scavenger_eye') ? 0.06 : 0) + (ctx.extra?.loot ?? 0) + pk('lootPct') / 100 + rankLoot(b.rank ?? 0) + tb('lootPct') / 100,
    stealth:
      (g.stats.stealth ?? 0) + set.stealth + (stance.stealth ?? 0) + (bg.perks.stealth ?? 0) + (has('unseen') ? 0.05 : 0) + (has('ghost_walkers') ? 0.1 : 0) - (loud ? GAME.LOUD_STEALTH_PENALTY : 0) + tb('stealthPct') / 100,
    detect: (g.stats.detect ?? 0) + (bg.perks.detect ?? 0) + (has('lookout') ? 0.1 : 0) + (has('eye_storm') ? 0.25 : 0) + pk('detectPct') / 100,
    dodge: cond?.dodge ? clamp(dodge + cond.dodge, -0.1, dodgeCap) : dodge,
    xp: (g.stats.xp ?? 0) + (ctx.extra?.xp ?? 0) + pk('xpPct') / 100 + meal.xp,
    expeditionTime: Math.min(0.5, -tb('expeditionTimePct') / 100 + (g.stats.expeditionTime ?? 0) + set.expeditionTime + (has('homing') ? 0.1 : 0) - pk('durationPct') / 100),
    healing: (g.stats.healing ?? 0) + set.healing + tb('healingPct') / 100 + pk('healingPct') / 100,
    discovery: (g.stats.discovery ?? 0) + intOver * INT_RULES.discoveryPerPoint + pk('discoveryPct') / 100,
    craftSpeed: Math.min(0.6, (g.stats.craftSpeed ?? 0) + set.craftSpeed + (bg.perks.craftSpeed ?? 0) + (has('tinker') ? 0.15 : 0) + Math.min(INT_RULES.craftSpeedMax, intOver * INT_RULES.craftSpeedPerPoint) - pk('craftTimePct') / 100),
    vs,
    ranged,
    loud,
    firearm: g.firearm,
    weaponClass: g.weaponClass,
    adrenaline: tb('attackBelow30HpPct') / 100,
    executioner: tb('damageVsLowHpPct') / 100,
    ambushBonus: (stance.ambush ?? 0) + set.ambush + tb('ambushDamagePct') / 100 + pk('ambushDamagePct') / 100 + (g.stats.ambush ?? 0),
    fieldMedic: tb('hpAfterWinPct') / 100 + (has('second_wind') ? 0.05 : 0) + pk('hpAfterWinPct') / 100,
    passives: g.passives,
    // 11. 10. 2026: the Foundry set's melee resistance adds to the affixes' (together at most 50%).
    meleeResist: Math.min(0.5, (g.stats.meleeResist ?? 0) + set.meleeResist),
    rangedResist: Math.min(0.5, g.stats.rangedResist ?? 0),
    luck: g.stats.luck ?? 0,
    postFightHeal: set.postFightHeal + pk('hpAfterFightPct') / 100,
    repairDiscount: set.repairDiscount - pk('repairCostPct') / 100,
    dismantleBonus: set.dismantleScrap + pk('dismantleScrapPct') / 100,
    setHooks: sets.hooks,
    initiative: initiative + meal.initiative,
    load,
    armor: g.stats.armor ?? 0,
    encounter: Math.min(0.5, g.stats.encounter ?? 0),
    alwaysFirst: perkFlag(fx, 'neverAttackedFirst', pc),
    perkFx: fx,
    // The skill tree (K10): what acts outside the numbers above.
    openingShotDamage,
    openingShotCrit,
    dodgeCap,
    initiativeMult,
    escapeHalfFull: tb('escapeWhenHalfFullPct') / 100,
    noTrade: tb('noTrade') > 0,
    encounterMod: tb('encounterChancePct') / 100,
    extraItem: tb('extraItemPct') / 100,
    mealBoost: mealBoostNow,
    mealFood: pk('mealFoodPct') / 100,
    craftMaterials: tb('craftMaterialsPct') / 100,
    craftRollPts: tb('craftRollMinPts'),
  };
}

/** Highest dodge without Phantom (krug 5, F2 / K10.4). */
export const DODGE_CAP = 0.3;

/**
 * What INT over the start does (29. 9. 2026: the numbers in one place, the Guide reads them): cheaper
 * repairs, faster crafting, more special finds, better trades in encounters. Materials and craft rolls
 * are `INT_CRAFT` (items/upgrade.ts).
 */
export const INT_RULES = { repairPerPoint: 0.015, repairMax: 0.4, craftSpeedPerPoint: 0.015, craftSpeedMax: 0.3, discoveryPerPoint: 0.01, tradePerPoint: 0.01, tradeMax: 0.2 };

/** INT discount on repairs. */
export function intDiscount(int: number): number {
  return clamp((int - GAME.BASE_ATTR) * INT_RULES.repairPerPoint, 0, INT_RULES.repairMax);
}

export function vsMultiplier(att: CombatStats, defenderType: EnemyType): number {
  return 1 + (att.vs[defenderType] ?? 0);
}

// ---- Energy and HP over time -------------------------------------------------

/**
 * 7. 10. 2026 (owner, energy test): what the energy rules look at besides the level: the Medical Tent's level (the maximum
 * by the tent, `ENERGY_RULES.maxByTent`) and when the account started (the first days' refill, `ENERGY_RULES.firstDays`).
 */
export interface EnergyCtx {
  tent?: number;
  createdAt?: number;
}

/**
 * The most energy a survivor holds (rewards may go over it): since the owner's decision of 7. 10. 2026 10 + 2 a Medical
 * Tent level (`maxByTent`: 10, 12 … 20); before, 20 on every level (1. 10. 2026, C5). The level counts only for the
 * simulations' rules of 30. 9. (`ENERGY_RULES.perLevel`: 9 + level).
 */
export function energyMax(level: number, ctx: EnergyCtx = {}): number {
  const t = ENERGY_RULES.maxByTent;
  if (t) return t.base + t.perLevel * Math.max(0, Math.floor(ctx.tent ?? 0));
  const p = ENERGY_RULES.perLevel;
  return ENERGY_RULES.max + (p ? Math.max(0, Math.floor(level) - 1) * p.maxPlus : 0);
}

/**
 * Minutes per point of energy: 2 hours on every level since 1. 10. 2026 (C5; the sims' rules of 30. 9.: 4 hours from level 10).
 * `at` and `ctx.createdAt`: inside the account's first days (`ENERGY_RULES.firstDays`, a rule being tested) the faster pace.
 */
export function energyRegenMinutes(level: number, at?: number, ctx: EnergyCtx = {}): number {
  const f = ENERGY_RULES.firstDays;
  if (f && at != null && ctx.createdAt != null && at < ctx.createdAt + f.days * 86_400_000) return f.regenMinutes;
  const p = ENERGY_RULES.perLevel;
  return p && level >= p.slowFrom ? p.slowMinutes : ENERGY_RULES.regenMinutes;
}

/** The end of the first days' faster refill (null without one). */
function fastUntil(ctx: EnergyCtx): number | null {
  const f = ENERGY_RULES.firstDays;
  return f && ctx.createdAt != null ? ctx.createdAt + f.days * 86_400_000 : null;
}

/** When energy is back to full at this level (null when it already is). */
export function energyFullAt(stored: number, updatedAt: number, now: number, level: number, speed = 1, ctx: EnergyCtx = {}): number | null {
  const e = energyNow(stored, updatedAt, now, level, speed, ctx);
  const max = energyMax(level, ctx);
  if (e.energy >= max) return null;
  // Point by point, the pace of the moment (the first days' end can fall in between).
  let t = now + e.nextInMs;
  for (let n = e.energy + 1; n < max; n++) t += (energyRegenMinutes(level, t, ctx) * 60_000) / speed;
  return t;
}

/** What the "Your energy is full" job remembers of a player: the player's day it last pinged and the fill it last looked at. */
export interface EnergyPingState {
  day: string;
  fullAt: number;
}

/**
 * 20. 10. 2026 (owner, pilot report): "Your energy is full", for players who turned it on in Settings (off by default).
 * Each time the energy fills (`fullAt`, from what was stored at the last spend) is looked at once: `send` pings, `skip`
 * remembers it without a ping (the player is online and sees it, the survivor is out and the return pings them, or there was
 * a ping on the player's day already), `none` leaves it for later (not full yet, already looked at, or night: the morning).
 */
export function energyPingAction(p: { fullAt: number | null; now: number; today: string; online: boolean; away: boolean; night: boolean; last: EnergyPingState | null }): 'send' | 'skip' | 'none' {
  if (p.fullAt == null || p.fullAt > p.now) return 'none';
  if (p.last && p.last.fullAt >= p.fullAt) return 'none';
  if (p.online || p.away || p.last?.day === p.today) return 'skip';
  return p.night ? 'none' : 'send';
}

/**
 * Energy now at this level (its pace and its maximum), from what was stored at `updatedAt`. With the first days' faster
 * refill (a rule being tested) the point under way at their end keeps its share at the new pace, as on a level up.
 */
export function energyNow(stored: number, updatedAt: number, now: number, level: number, speed = 1, ctx: EnergyCtx = {}): { energy: number; nextInMs: number; updatedAt: number } {
  const max = energyMax(level, ctx);
  if (stored >= max) return { energy: stored, nextInMs: 0, updatedAt: now };
  const until = fastUntil(ctx);
  let energy = stored;
  let t = updatedAt;
  // The first days, then the rest (no first days: one stretch).
  if (until != null && t < until) {
    const fast = (energyRegenMinutes(level, t, ctx) * 60_000) / speed;
    const end = Math.min(now, until);
    const gained = Math.floor(Math.max(0, end - t) / fast);
    energy = Math.min(max, energy + gained);
    if (energy >= max) return { energy, nextInMs: 0, updatedAt: now };
    t += gained * fast;
    if (now <= until) return { energy, nextInMs: Math.max(0, fast - (now - t)), updatedAt: t };
    // The point under way at the end of the first days goes on at the normal pace with its share done.
    const share = (until - t) / fast;
    t = until - share * ((energyRegenMinutes(level, until, ctx) * 60_000) / speed);
  }
  const period = (energyRegenMinutes(level, now, ctx) * 60_000) / speed;
  const gained = Math.floor(Math.max(0, now - t) / period);
  energy = Math.min(max, energy + gained);
  if (energy >= max) return { energy, nextInMs: 0, updatedAt: now };
  const newUpdated = t + gained * period;
  return { energy, nextInMs: Math.max(0, period - (now - newUpdated)), updatedAt: newUpdated };
}

/**
 * 30. 9. 2026: energy on a level up from `from` to `to`. What refilled up to now counts at the old
 * level; the point under way keeps its share at the new pace (half of 2 hours becomes half of 4 on
 * level 10), so nothing is lost or gained; a survivor full at the old maximum starts refilling now
 * (the maximum grew), never with the hours spent full. Since 1. 10. 2026 (C5) the pace and the maximum
 * are the same on every level, so a level up changes nothing; it still matters for the sims' rules of 30. 9.
 */
export function energyOnLevelUp(stored: number, updatedAt: number, now: number, from: number, to: number, speed = 1, ctx: EnergyCtx = {}): { energy: number; updatedAt: number } {
  const e = energyNow(stored, updatedAt, now, from, speed, ctx);
  if (e.energy >= energyMax(from, ctx)) return { energy: e.energy, updatedAt: now };
  const share = Math.min(1, Math.max(0, (now - e.updatedAt) / ((energyRegenMinutes(from, now, ctx) * 60_000) / speed)));
  return { energy: e.energy, updatedAt: now - share * ((energyRegenMinutes(to, now, ctx) * 60_000) / speed) };
}

export function campRegenPerHour(medicalTentLevel: number, clanRegen = 0): number {
  return GAME.CAMP_REGEN_PER_HOUR + medicalTentLevel * GAME.MEDICAL_TENT_REGEN_PER_LEVEL + clanRegen;
}

export function hpNow(stored: number, updatedAt: number, now: number, hpMax: number, regenPerHour: number, speed = 1): number {
  if (stored >= hpMax) return hpMax;
  const hours = (Math.max(0, now - updatedAt) / 3_600_000) * speed;
  return Math.min(hpMax, Math.floor(stored + hpMax * regenPerHour * hours));
}

// ---- Carry --------------------------------------------------------------------


/** New players are protected until level 5 or for their first 3 days, whichever ends first. */
export function isProtectedNewPlayer(level: number, createdAt: number, now: number): boolean {
  return level < GAME.NEW_PLAYER_PROTECTION_LEVEL && now - createdAt < GAME.NEW_PLAYER_PROTECTION_DAYS * 86_400_000;
}

export function inPowerRange(a: number, b: number, range = GAME.PVP_POWER_RANGE): boolean {
  if (a <= 0 || b <= 0) return false;
  return b >= a * (1 - range) && b <= a * (1 + range);
}
